import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Button
} from '@mui/material';
import { 
  ExpandMore,
  Search,
  QuestionAnswer,
  AttachMoney,
  Gavel,
  Security,
  Support
} from '@mui/icons-material';

const FAQ = () => {
  const [expandedAccordion, setExpandedAccordion] = useState('general0');
  const [searchTerm, setSearchTerm] = useState('');

  const faqCategories = [
    {
      category: "General",
      icon: <QuestionAnswer />,
      color: "primary",
      questions: [
        {
          question: "What is DS360 and what services do you provide?",
          answer: "DS360 is a comprehensive platform that provides real estate investors with access to foreclosure property data, market analysis tools, and investment resources. We specialize in New Jersey foreclosure auctions and provide detailed property information, valuation estimates, and investment analysis tools to help you make informed decisions."
        },
        {
          question: "How accurate is your property data?",
          answer: "Our data is sourced directly from county records, court filings, and public databases. We update our information daily and employ verification processes to ensure accuracy. However, we always recommend conducting your own due diligence before making investment decisions, as property conditions and legal statuses can change."
        },
        {
          question: "What geographic areas do you cover?",
          answer: "Currently, we provide comprehensive coverage for all counties in New Jersey. We are expanding to additional states on the East Coast. If you're interested in a specific area not currently covered, please contact us to learn about our expansion timeline."
        },
        {
          question: "How often is your data updated?",
          answer: "We update our property database daily with new filings, auction schedules, and status changes. Market valuations are refreshed weekly, and our AI-powered analytics are continuously learning from new data to improve accuracy."
        }
      ]
    },
    {
      category: "Pricing & Subscriptions",
      icon: <AttachMoney />,
      color: "success",
      questions: [
        {
          question: "What subscription plans do you offer?",
          answer: "We offer three main subscription tiers: Basic ($49/month) includes access to property listings and basic filters; Pro ($99/month) adds advanced analytics, ROI calculators, and market comparables; Premium ($199/month) includes all features plus priority support, custom alerts, and API access."
        },
        {
          question: "Is there a free trial available?",
          answer: "Yes! We offer a 14-day free trial that includes access to our Basic plan features. No credit card is required to start your trial, and you can upgrade or cancel at any time during the trial period."
        },
        {
          question: "Can I cancel my subscription at any time?",
          answer: "Absolutely. You can cancel your subscription at any time through your account settings. Your access will continue until the end of your current billing period, and you won't be charged for the following period."
        },
        {
          question: "Do you offer discounts for annual subscriptions?",
          answer: "Yes! Annual subscriptions receive a 20% discount compared to monthly billing. We also offer special pricing for real estate professionals, investors with multiple properties, and educational institutions."
        }
      ]
    },
    {
      category: "Foreclosure Process",
      icon: <Gavel />,
      color: "warning",
      questions: [
        {
          question: "How does the foreclosure auction process work in New Jersey?",
          answer: "New Jersey follows a judicial foreclosure process. After a homeowner defaults, the lender files a lawsuit, the court orders a sale, and the property is auctioned by the sheriff. The process typically takes 6-12 months from filing to auction. Properties are sold to the highest bidder above the upset price."
        },
        {
          question: "What is an upset price?",
          answer: "The upset price is the minimum bid amount set by the court, typically equal to the outstanding loan balance plus legal costs and fees. Bidding starts at this amount, and the property cannot be sold for less unless there are no bidders."
        },
        {
          question: "What happens if no one bids at the auction?",
          answer: "If no one bids at the auction, the property becomes REO (Real Estate Owned) by the lender. The bank then typically lists the property for sale through traditional real estate channels, often at a higher price than the upset value."
        },
        {
          question: "Can I inspect a property before the auction?",
          answer: "Interior inspections are usually not allowed before foreclosure auctions. You can typically view the exterior and research the property through our platform, public records, and drive-by visits. Factor this limitation into your bidding strategy and budget for potential repairs."
        }
      ]
    },
    {
      category: "Platform Features",
      icon: <Security />,
      color: "info",
      questions: [
        {
          question: "How do your property valuations work?",
          answer: "Our valuations combine automated valuation models (AVM) with local market data, recent comparable sales, and AI-powered analysis. We provide estimated market values, potential rental income, and renovation cost estimates to help you calculate potential returns."
        },
        {
          question: "Can I set up custom property alerts?",
          answer: "Yes! Pro and Premium subscribers can create custom alerts based on location, price range, property type, auction dates, and ROI potential. You'll receive notifications via email or SMS when properties matching your criteria are added to our database."
        },
        {
          question: "Do you provide financing options?",
          answer: "While we don't directly provide financing, we have partnerships with lenders who specialize in investment property loans, hard money lending, and auction financing. Premium subscribers get access to our lender network and preferred rates."
        },
        {
          question: "Is my personal and financial information secure?",
          answer: "Yes, we take security seriously. We use bank-level encryption, secure servers, and follow industry best practices for data protection. We never share your personal information with third parties without your consent, and our payment processing is PCI DSS compliant."
        }
      ]
    }
  ];

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => 
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1B5E20',
            mb: 2 
          }}>
            Frequently Asked Questions
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
            mb: 4
          }}>
            Find answers to common questions about our platform, foreclosure investing, 
            and how to get the most out of DS360.
          </Typography>

          {/* Search */}
          <TextField
            fullWidth
            placeholder="Search for answers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ maxWidth: 500, mx: 'auto' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#666' }} />
                </InputAdornment>
              )
            }}
          />
        </Box>

        {/* FAQ Categories */}
        <Grid container spacing={4}>
          {filteredCategories.map((category, categoryIndex) => (
            <Grid item xs={12} key={categoryIndex}>
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ p: 0 }}>
                  {/* Category Header */}
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: '#e8f5e8',
                    display: 'flex', 
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <Box sx={{ color: '#1B5E20', mr: 2 }}>
                      {category.icon}
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#1B5E20', mr: 2 }}>
                      {category.category}
                    </Typography>
                    <Chip 
                      label={`${category.questions.length} questions`}
                      size="small"
                      color={category.color}
                    />
                  </Box>

                  {/* Questions */}
                  <Box sx={{ p: 2 }}>
                    {category.questions.map((faq, faqIndex) => {
                      const panelId = `${category.category.toLowerCase()}${faqIndex}`;
                      return (
                        <Accordion
                          key={faqIndex}
                          expanded={expandedAccordion === panelId}
                          onChange={handleAccordionChange(panelId)}
                          sx={{ 
                            boxShadow: 'none',
                            '&:before': { display: 'none' },
                            border: '1px solid #e0e0e0',
                            mb: 1,
                            borderRadius: 1
                          }}
                        >
                          <AccordionSummary 
                            expandIcon={<ExpandMore />}
                            sx={{ 
                              bgcolor: expandedAccordion === panelId ? '#f8f9fa' : 'white',
                              '&:hover': { bgcolor: '#f8f9fa' }
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {faq.question}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            <Typography variant="body1" sx={{ 
                              color: '#666',
                              lineHeight: 1.7
                            }}>
                              {faq.answer}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* No Results */}
        {filteredCategories.length === 0 && searchTerm && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" sx={{ color: '#666', mb: 2 }}>
              No results found for "{searchTerm}"
            </Typography>
            <Typography variant="body1" sx={{ color: '#999', mb: 3 }}>
              Try a different search term or browse our categories above.
            </Typography>
            <Button 
              variant="outlined"
              onClick={() => setSearchTerm('')}
              sx={{ color: '#1B5E20', borderColor: '#1B5E20' }}
            >
              Clear Search
            </Button>
          </Box>
        )}

        {/* Contact Support */}
        <Card sx={{ mt: 6, bgcolor: '#1B5E20', color: 'white' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Support sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
              Still Need Help?
            </Typography>
            <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
              Can't find what you're looking for? Our support team is here to help!
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item>
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ 
                    bgcolor: 'white',
                    color: '#1B5E20',
                    fontWeight: 600,
                    px: 3
                  }}
                >
                  Contact Support
                </Button>
              </Grid>
              <Grid item>
                <Button 
                  variant="outlined" 
                  size="large"
                  sx={{ 
                    color: 'white',
                    borderColor: 'white',
                    fontWeight: 600,
                    px: 3
                  }}
                >
                  Live Chat
                </Button>
              </Grid>
            </Grid>
            <Box sx={{ mt: 3, opacity: 0.8 }}>
              <Typography variant="body1">
                Email: connect@ds360investment.com • Phone: (201) 912-0705
              </Typography>
              <Typography variant="body2">
                Support Hours: Monday-Friday 9AM-6PM PST
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default FAQ;














